import { useCallback, useEffect, useRef, useState } from "react";
import { SourceDto, MonsterDto, TranslatedText, SpellDto } from "../../../Types/ApiTypes";
import MonsterModal from "./MonsterModal";
import { useTranslation } from "react-i18next";
import { distance } from "../../../Types/TableTypes";
import MonsterRowItemComponent from "./MonsterRowItemComponent";
import { getTranslation } from "../../../Utils/Converters";

interface MonsterTableComponentProps{
    allMonsters: MonsterDto[],
    allSources: SourceDto[],
    setAllMonsters: (allMonsters: MonsterDto[]) => void
}

const MonsterTableComponent = (props: MonsterTableComponentProps) => {
    const [sortAttribute, setSortAttribute] = useState("id");
    const [sortDirection, setSortDirection] = useState("ASC");
    const [monsters, setMonsters] = useState(props.allMonsters);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [nameSearchValue, setNameSearchValue] = useState("");
    const [sourceFilterValue, setSourceFilterValue] = useState([] as SourceDto[]);
    // const [timeFilterValue, setTimeFilterValue] = useState([] as string[]);
    const [sourceFilterOpen, setSourceFilterOpen] = useState(false);
    // const [timeFilterOpen, setTimeFilterOpen] = useState(false);
    // const [concFilterOpen, setConcFilterOpen] = useState(false);
    // const [concFilterValue, setConcFilterValue] = useState([] as boolean[])
    // const [timeFilters, setTimeFilters] = useState([] as string[]);
    // const [classFilters, setClassFilters] = useState([] as string[]);
    const backgroundaRef = useRef<HTMLDivElement>(null);

    const { t } = useTranslation();
    const emptyMonster = {
        index: "",
        source: {} as SourceDto,
        name: {} as TranslatedText,
        size: {} as TranslatedText,
        type: {} as TranslatedText,
        alignment: {} as TranslatedText,
        armorClass: 0,
        hitPoints: 0,
        hitDice: "",
        hitPointsRoll: "",
        proficiencyBonus: 0,
        hasSpellCasting: false,
        walkDistance: {} as distance,
        flyDistance: {} as distance,
        swimDistance: {} as distance,
        climbDistance: {} as distance,
        burrowDistance: {} as distance,
        otherDistance: {} as TranslatedText,
        statStr: 0,
        statDex: 0,
        statCon: 0,
        statInt: 0,
        statWis: 0,
        statCha: 0,
        saveStr: 0,
        saveDex: 0,
        saveCon: 0,
        saveInt: 0,
        saveWis: 0,
        saveCha: 0,
        skills: {} as TranslatedText,
        damageVulnerabilities: {} as TranslatedText,
        damageResistances: {} as TranslatedText,
        damageImmunities: {} as TranslatedText,
        conditionImmunities: {} as TranslatedText,
        passivePerception: 0,
        darkvisionDistance: {} as distance,
        darkvisionText: {} as TranslatedText,
        blindsightDistance: {} as distance,
        blindsightText: {} as TranslatedText,
        tremorsenseDistance: {} as distance,
        tremorsenseText: {} as TranslatedText,
        truesightDistance: {} as distance,
        truesightText: {} as TranslatedText,
        languages: {} as TranslatedText,
        challenge: {} as TranslatedText,
        challengeCr: 0 as number,
        challengeXP: 0 as number,
        challengePB: 0 as number,
        equipmentText: {} as TranslatedText,
        traitsText: {} as TranslatedText,
        traits: [] as { title: TranslatedText; description: TranslatedText; }[],
        actionsText: {} as TranslatedText,
        actions: [] as { title: TranslatedText; description: TranslatedText;}[],
        bonusActionsText: {} as TranslatedText,
        bonusActions: [] as { title: TranslatedText; description: TranslatedText;}[],
        legendaryActionsText: {} as TranslatedText,
        legendaryActions: [] as { title: TranslatedText; description: TranslatedText;}[],
        reactionsText: {} as TranslatedText,
        reactions: [] as { title: TranslatedText; description: TranslatedText;}[],
        spellCharacteristic: {en: "", it: ""},
        //     id: 0,
        //     index: "",
        //     name: {} as TranslatedText,
        //     fullName: {} as TranslatedText,
        //     description: {} as TranslatedText
        // },
        spellSave: 0,
        spellAttackMod: 0,
        spellGroups: [] as {
            id: number,
            name: TranslatedText,
            hasSlotsGroup: boolean,
            hasSlotsEach: boolean,
            maxSlots: number,
            spells: SpellDto[]
        }[]
    }

    const filterAndSort = useCallback(() =>{
        let filteredMonsters = props.allMonsters;
        // console.log("sort "+sortAttribute+" "+sortDirection)
        // let times = [] as string [];
        // let classes = [] as string [];

        // filteredMonsters.forEach((monster) => {
        //     if (!times.includes(getTranslation(monster.castingTime))) 
        //         times.push(getTranslation(monster.castingTime))
        //     if (!classes.includes(getTranslation(monster.classes))) 
        //         classes.push(getTranslation(monster.classes))
        // })

        // setTimeFilters(times);
        // setClassFilters(classes);

        //filter concentration
        // if (concFilterValue.length > 0)
        //     filteredMonsters = filteredMonsters.filter((monster) => {
        //         return concFilterValue.some(conc => {
        //             return monster.concentration !== undefined && conc === monster.concentration
        //     });
        // });

        //filter sources
        if (sourceFilterValue.length > 0)
            filteredMonsters = filteredMonsters.filter((monster) => {
                return monster.source ? sourceFilterValue.some(source => {
                    return source.code === (monster.source ? monster.source.code : undefined)
            }) : false;
        });

        //filter casting time
        // if (timeFilterValue.length > 0)
        //     filteredMonsters = filteredMonsters.filter((monster) => {
        //         return monster.castingTime ? timeFilterValue.some(time => {
        //             return time === getTranslation(monster.castingTime)
        //     }) : false;
        // });

        //search name
        if (nameSearchValue.length > 0){
            filteredMonsters = filteredMonsters.filter((monster: MonsterDto) => getTranslation(monster.name).toLowerCase().includes(nameSearchValue.toLowerCase()));
        }
        //sort by id
        if (sortAttribute === "id"){
            filteredMonsters.sort((a, b) => sortNumbers(a.id!, b.id!, sortDirection));
        }
        // //sort by index
        // if (sortAttribute === "index"){
        //     filteredMonsters.sort((a, b) => sortStrings(a.index, b.index, sortDirection));
        // }
        //sort by source
        if (sortAttribute === "source"){
            filteredMonsters.sort((a, b) => sortStrings(a.source? a.source.code : "", b.source ? b.source.code : "", sortDirection));
        }
        //sort by name
        if (sortAttribute === "name"){
            filteredMonsters.sort((a, b) => sortStrings(getTranslation(a.name), getTranslation(b.name), sortDirection));
        }
        //sort by school
        // if (sortAttribute === "school"){
        //     filteredMonsters.sort((a, b) => sortStrings(getTranslation(a.school), getTranslation(b.school), sortDirection));
        // }

        
        setMonsters([...filteredMonsters]);        
    },[props.allMonsters, sortAttribute, sortDirection, nameSearchValue, sourceFilterValue]);//,timeFilterValue, concFilterValue]);


    useEffect(() => {filterAndSort()},[filterAndSort])
    function deleteMonster(id?: number){
        props.setAllMonsters(props.allMonsters.filter((monster: MonsterDto) => monster.id !== id))
    }

    function sortNumbers(a: number, b: number, direction: string){
        return direction === "ASC" ? (a - b) : (b - a);
    }

    function sortStrings(a: string, b: string, direction: string){
        if (a > b)
            return direction === "ASC" ? 1 : -1;
        if (a < b)
            return direction === "ASC" ? -1 : 1;
        return 0;
    }

    return (
        <>
            <div className="d-flex black justify-content-between px-4 py-2 header navbar text-white bg-dark">
            </div>
            <table className='table table-responsive text-start'>
                <thead style={{top: "80px", zIndex: "2"}}>
                    <tr>
                        <th scope="col" className="">
                            <span className="d-flex">
                                <span className="d-flex "
                                    role="button" onClick={(e) => {
                                        if (sortAttribute !== "id"){
                                            setSortAttribute("id");
                                            setSortDirection("ASC");
                                        }else{
                                            if(sortDirection === "ASC")
                                                setSortDirection("DESC");
                                            else
                                                setSortDirection("ASC");
                                        }
                                    }}>
                                    <span className="d-flex">
                                        {t("monsterlist-id")}
                                        &nbsp;
                                        {sortAttribute !== "id" && <i className="bi bi-sort-up text-secondary"></i>}
                                        {sortAttribute === "id" && sortDirection === "ASC" && <i className="bi bi-sort-up"></i>}
                                        {sortAttribute === "id" && sortDirection === "DESC" && <i className="bi bi-sort-down"></i>}
                                    </span>
                                </span>
                                <span>&nbsp;</span>
                            </span>
                        </th>
                        <th scope="col" className="">
                            <span className="d-flex">
                                <span className="d-flex " role="button" onClick={(e) => {
                                        if (sortAttribute !== "source"){
                                            setSortAttribute("source");
                                            setSortDirection("ASC");
                                        }else{
                                            if(sortDirection === "ASC")
                                                setSortDirection("DESC");
                                            else
                                                setSortDirection("ASC");
                                        }
                                    }}>
                                    
                                    <span>{t("monsterlist-source")}</span>
                                    &nbsp;
                                    {sortAttribute !== "source" && <i className="bi bi-sort-up text-secondary"></i>}
                                    {sortAttribute === "source" && sortDirection === "ASC" && <i className="bi bi-sort-up"></i>}
                                    {sortAttribute === "source" && sortDirection === "DESC" && <i className="bi bi-sort-down"></i>}
                                </span>
                                &nbsp;
                                <span role="button" onClick={() => setSourceFilterOpen(!sourceFilterOpen)}><i className="bi bi-funnel"></i></span>
                                {sourceFilterOpen && 
                                    <>
                                        <div ref={backgroundaRef}
                                            style={{display: "block", opacity: "1", backgroundColor:"#00000000" ,width: "100%", height: "100%", position: "fixed", top: "0", left: "0"}} 
                                            tabIndex={-1} role="dialog" aria-hidden="true" 
                                            onClick={e=>{
                                                if(e.target === backgroundaRef.current)
                                                {
                                                    setSourceFilterOpen(false)
                                                }
                                            }}>
                                        </div>
                                    
                                        <span className="d-flex flex-column fs-5 p-4 dropdown-menu" style={{marginTop: "42px", fontWeight: "normal"}}>
                                            {
                                                props.allSources.map((source) => {
                                                    return (
                                                        <label key={source.id}>
                                                            <input type="checkbox" className="form-check-input"
                                                                checked={sourceFilterValue.filter((v) => {return v.code === source.code}).length>0}
                                                                onChange={(e) => {
                                                                    let value = sourceFilterValue;
                                                                    if (e.target.checked){
                                                                        value.push(source);
                                                                    }else{
                                                                        value = value.filter((v) => {
                                                                            return v.code !== source.code;
                                                                        });
                                                                    }
                                                                    
                                                                    setSourceFilterValue([...value]);
                                                                }}
                                                            ></input> {getTranslation(source.name)}
                                                        </label>
                                                    )
                                                })
                                            }
                                        </span>
                                    </>
                                }
                            </span>
                        </th>
                        {/* <th scope="col">
                            <span className="d-flex ">
                                <span className="d-flex"
                                    role="button" onClick={(e) => {
                                        if (sortAttribute !== "index"){
                                            setSortAttribute("index");
                                            setSortDirection("ASC");
                                        }else{
                                            if (sortDirection === "ASC")
                                                setSortDirection("DESC");
                                            else                                                
                                                setSortDirection("ASC");
                                        }
                                    }}>

                                    {t("monsterlist-index")}
                                    &nbsp;
                                    {sortAttribute !== "index" && <i className="bi bi-sort-up text-secondary"></i>}
                                    {sortAttribute === "index" && sortDirection === "ASC" && <i className="bi bi-sort-up"></i>}
                                    {sortAttribute === "index" && sortDirection === "DESC" && <i className="bi bi-sort-down"></i>}
                                </span>
                                <span>&nbsp;</span>
                            </span>
                        </th> */}
                        <th scope="col" className="">
                            <span className="d-flex ">
                                <span className="d-flex"
                                    role="button" onClick={(e) => {
                                        if (sortAttribute !== "name"){
                                            setSortAttribute("name");
                                            setSortDirection("ASC");
                                        }else{
                                            if (sortDirection === "ASC")
                                                setSortDirection("DESC");
                                            else
                                                setSortDirection("ASC");
                                        }
                                    }}>

                                    <span>{t("monsterlist-name")}</span>
                                    &nbsp;
                                    {sortAttribute !== "name" && <i className="bi bi-sort-up text-secondary"></i>}
                                    {sortAttribute === "name" && sortDirection === "ASC" && <i className="bi bi-sort-up"></i>}
                                    {sortAttribute === "name" && sortDirection === "DESC" && <i className="bi bi-sort-down"></i>}
                                </span>
                                &nbsp;
                                <input placeholder={t("monster-search")} className="form-control" value={nameSearchValue}
                                    onChange={(e) => {setNameSearchValue (e.target.value);}}
                                />
                            </span>
                        </th>
                        {/* <th scope="col" className="">
                            <span className="d-flex ">
                                <span className="d-flex"
                                    role="button" onClick={(e) => {
                                        if (sortAttribute !== "school"){
                                            setSortAttribute("school");
                                            setSortDirection("ASC");
                                        }else{
                                            if(sortDirection === "ASC")
                                                setSortDirection("DESC");
                                            else
                                                setSortDirection("ASC");
                                        }
                                    }}>

                                    <span>{t("monsterlist-school")}</span>
                                    &nbsp;
                                    {sortAttribute !== "school" && <i className="bi bi-sort-up text-secondary"></i>}
                                    {sortAttribute === "school" && sortDirection === "ASC" && <i className="bi bi-sort-up"></i>}
                                    {sortAttribute === "school" && sortDirection === "DESC" && <i className="bi bi-sort-down"></i>}
                                </span>
                                <span>&nbsp;</span>
                            </span>
                        </th> */}
                        {/* <th scope="col" className="">
                            <span className="d-flex ">
                                <span className="d-flex">
                                    {t("monsterlist-concentration")}
                                </span>
                                <span>&nbsp;</span>
                                <span role="button" onClick={() => setConcFilterOpen(!concFilterOpen)}><i className="bi bi-funnel"></i></span>
                                {concFilterOpen && 
                                    <>
                                        <div ref={backgroundaRef}
                                            style={{display: "block", opacity: "1", backgroundColor:"#00000000" ,width: "100%", height: "100%", position: "fixed", top: "0", left: "0"}} 
                                            tabIndex={-1} role="dialog" aria-hidden="true" 
                                            onClick={e=>{
                                                if(e.target === backgroundaRef.current)
                                                {
                                                    setConcFilterOpen(false)
                                                }
                                            }}>
                                        </div>
                                    
                                        <span className="d-flex flex-column fs-5 p-4 dropdown-menu" style={{marginTop: "42px", fontWeight: "normal"}}>
                                            
                                            <label>
                                                <input type="checkbox" className="form-check-input"
                                                    checked={concFilterValue.filter((v) => {return v === true}).length>0}
                                                    onChange={(e) => {
                                                        let value = concFilterValue;
                                                        if (e.target.checked){
                                                            value.push(true);
                                                        }else{
                                                            value = value.filter((v) => {
                                                                return v !== true;
                                                            });
                                                        }
                                                        
                                                        setConcFilterValue([...value]);
                                                    }}
                                                ></input> {"true"}
                                            </label>
                                            <label>
                                                <input type="checkbox" className="form-check-input"
                                                    checked={concFilterValue.filter((v) => {return v === false}).length>0}
                                                    onChange={(e) => {
                                                        let value = concFilterValue;
                                                        if (e.target.checked){
                                                            value.push(false);
                                                        }else{
                                                            value = value.filter((v) => {
                                                                return v !== false;
                                                            });
                                                        }
                                                        
                                                        setConcFilterValue([...value]);
                                                    }}
                                                ></input> {"false"}
                                            </label>
                                        </span>
                                    </>
                                }
                            </span>
                        </th> */}
                        {/* <th scope="col" className="">
                            <span className="d-flex ">
                                <span className="d-flex">
                                    {t("monsterlist-classes")}
                                </span>
                                <span>&nbsp;</span>
                            </span>
                        </th> */}
                        {/* <th scope="col" className="">
                            <span className="d-flex ">
                                <span className="d-flex">
                                    {t("monsterlist-castingtime")}
                                </span>
                                <span>&nbsp;</span>
                                <span role="button" onClick={() => setTimeFilterOpen(!sourceFilterOpen)}><i className="bi bi-funnel"></i></span>
                                {timeFilterOpen && 
                                    <>
                                        <div ref={backgroundaRef}
                                            style={{display: "block", opacity: "1", backgroundColor:"#00000000" ,width: "100%", height: "100%", position: "fixed", top: "0", left: "0"}} 
                                            tabIndex={-1} role="dialog" aria-hidden="true" 
                                            onClick={e=>{
                                                if(e.target === backgroundaRef.current)
                                                {
                                                    setTimeFilterOpen(false)
                                                }
                                            }}>
                                        </div>
                                    
                                        <span className="d-flex flex-column fs-5 p-4 dropdown-menu" style={{marginTop: "42px", fontWeight: "normal"}}>
                                            {
                                                timeFilters.map((time) => {
                                                    return (
                                                        <label>
                                                            <input type="checkbox" className="form-check-input"
                                                                checked={timeFilterValue.filter((v) => {return v === time}).length>0}
                                                                onChange={(e) => {
                                                                    let value = timeFilterValue;
                                                                    if (e.target.checked){
                                                                        value.push(time);
                                                                    }else{
                                                                        value = value.filter((v) => {
                                                                            return v !== time;
                                                                        });
                                                                    }
                                                                    
                                                                    setTimeFilterValue([...value]);
                                                                }}
                                                            ></input> {time}
                                                        </label>
                                                    )
                                                })
                                            }
                                        </span>
                                    </>
                                }
                            </span>
                        </th> */}
                        <th style={{fontWeight: "normal"}}>
                            <button className="btn btn-primary" 
                                onClick={() => setIsModalOpen(true)}
                            >{t("table-add")}</button>
                            {isModalOpen && 
                            <MonsterModal
                                monster={emptyMonster}
                                updateMonster={(monster: MonsterDto) => props.allMonsters.push(monster)}
                                deleteMonster={deleteMonster}
                                closeModal={()=>{setIsModalOpen(false)}}
                            />}
                        </th>
                    </tr>
                    <tr>
                        <td colSpan={11} style={{borderWidth: "0", padding: "0"}}>
                            <div style={{ borderStyle: "solid", borderWidth: "2px"}}></div>
                        </td>
                    </tr>
                </thead>
                <tbody className="">
                    {monsters.map((monster: MonsterDto, index: number) =>(
                        <MonsterRowItemComponent
                            key={monster.id}
                            monster={monster}
                            deleteMonster={deleteMonster}
                        />
                    ))}
                </tbody>
            </table>
        </>
    )
}

export default MonsterTableComponent;